/**
 * The punch list's "Download for Excel": the rows on screen, as CSV, with the
 * spreadsheets' columns in their order (Jeremy, 27 Oct 2026).
 *
 * CSV, not .xlsx: nothing in this repo writes a workbook in the browser, and a
 * library for one button is not worth it. A byte-order mark and CRLF, so Excel
 * opens it as UTF-8 and keeps the line breaks inside a cell.
 *
 * **NO CELL MAY START A FORMULA.** The rows are problem reports, and anyone
 * signed in can type one: a report whose text starts with `=`, `+`, `-`, `@`, a
 * tab or a carriage return would run as a formula when the file is opened in
 * Excel (CSV injection). Such a value is prefixed with `'`, which Excel shows
 * as plain text. `web/test/punch-csv.test.ts` covers each leading character.
 */
import type { PunchItem } from "@fieldpath/shared";

export const CSV_COLUMNS: readonly (readonly [string, (i: PunchItem) => string | null])[] = [
  ["Concern #", (i) => i.label],
  ["Screen", (i) => i.screen],
  ["Concern", (i) => i.concern],
  ["Other notes", (i) => i.otherNotes],
  ["Example URL", (i) => i.exampleUrl],
  ["Reported by", (i) => i.reportedBy],
  ["Type", (i) => i.type],
  ["Claude response", (i) => i.claudeResponse],
  ["Status", (i) => i.status],
  // Not one of the spreadsheets' columns: added with the field (F152, 30 Oct 2026).
  ["Priority", (i) => (i.priority === null ? null : String(i.priority))],
  ["PR / commit", (i) => i.prCommit],
  ["Jeremy's notes / decision", (i) => i.jeremyNotes],
];

/** The characters that make a spreadsheet read a cell as a formula. */
const FORMULA_START = /^[=+\-@\t\r]/;

/** One quoted CSV cell, never a formula. */
export function csvCell(value: string | null): string {
  let s = value ?? "";
  if (FORMULA_START.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}

export function punchCsv(items: readonly PunchItem[]): string {
  const lines = [CSV_COLUMNS.map(([header]) => csvCell(header)).join(",")];
  for (const item of items) lines.push(CSV_COLUMNS.map(([, get]) => csvCell(get(item))).join(","));
  return `﻿${lines.join("\r\n")}\r\n`;
}
